import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';

import { RCListingCard, RCNudgeProps, RCPriceBlockProps } from '@reverbdotcom/cadence/components';
import { ThemeType } from '@reverbdotcom/cadence/components/types';

import ConditionDisplay from './condition_display';
import PriceDisplay from './price_display';
import PriceWithShipping from './price_with_shipping';
import ImageCarousel from './image_carousel';
import ListingCardNudge from './listing_card_nudge';
import {
  ListingCardFields,
  FinancingFields,
  InOtherCartsCardData,
  core_apimessages_Address as Address,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import { WatchBadgeFragment, WatchBadge, DisplayStyle } from './watch_badge';
import { WatchParentComponentName } from '../event_tracking/watchlist';
import CoreLink from './core_link';
import { isMarketplacePage, isUsingProximityFeatures, ITEM_WITH_SLUG_ROUTE, listingItemPagePath } from '../url_helpers';
import { withUserContext, IUserContext, IUser } from './user_context_provider';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { WEB } from '../constants';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { ListingOfferMetadata } from './listing_offer_metadata';
import ListingShopCountryNudge from './listing_shop_country_nudge';
import { BRAND_NEW } from '../condition_helpers';
import ListingCardSignals, { ListingWithSignals } from './listing_card_signals';
import { isGreatValue } from '../listing_great_value';
import { isNewListing } from '../newListingNudge';

import {
  getNudges,
  getEyebrow,
  getPriceNudgeProps,
  buildListingForBuyer,
  ListingForBuyerFragment,
  getA11yPriceDescription,
} from '../listing_card_helpers';
import { ThemeContext } from './theme_context_provider';

const DISPLAY_STYLE = 'squareCard';

type ListingCardListingFields = ListingCardFields.Fragment & FinancingFields.Fragment & InOtherCartsCardData.Fragment & ListingWithSignals;
interface IProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: ListingCardListingFields;
  position?: number;
  inMobileApp?: boolean;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  showEstimatedMonthlyPayment?: boolean;
  trackingQuery?: string;
  showFlag?: boolean;
  // please use a WatchBadgeParentComponent value when possible instead of a plain string!
  watchBadgeParentComponentName?: WatchParentComponentName | string;
  displayGreatValue?: boolean;
}

export class ListingCard extends React.Component<IProps & IUserContext & WithRouterProps, null> {
  static contextType = ThemeContext;

  static defaultProps: Partial<IProps> = {
    displayGreatValue: true,
  };

  get decoratedListing() {
    return buildListingForBuyer(this.props.listing, this.props.combinedShippingDiscount);
  }

  isBumped() {
    return shouldShowBumpTag(this.props.listing);
  }

  returnPolicyDays() {
    const { listing: { condition, shop, soldAsIs } } = this.props;
    if (soldAsIs || !condition?.conditionSlug) return 0;
    const returnPolicyDays = condition.conditionSlug === BRAND_NEW ? shop.returnPolicy?.newReturnWindowDays : shop.returnPolicy?.usedReturnWindowDays;
    return (returnPolicyDays || 0);
  }

  listingUrl() {
    if (this.props.soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: this.props.listing.slug,
          id: this.props.listing.id,
          show_sold: true,
        })
      );
    }

    return (
      listingItemPagePath(this.props.listing)
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText() {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (!this.hasDiscount()) { return null; }

    return (
      <div className="grid-card__original-price">
        <span className="mr-space td-line-through">
          {this.props.listing.pricing.originalPrice.display}
        </span>
        <span className="grid-card__discount">
          {this.ribbonText()}
        </span>
      </div>
    );
  }

  renderMetadata() {
    if (this.props.hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={this.props.listing} />
    );
  }

  clickEvent() {
    return {
      experiments: formatExperimentsForEventAttributes(
        this.props.user,
        [
          experiments.LISTING_CARD_LOCATION,
          experiments.DUMMY,
          experiments.DATA_DRIVEN_WELL_PRICED_LISTINGS,
        ],
      ),
      query: this.props.trackingQuery,
      shownAsBumped: this.isBumped(),
      ...buildListingClickEvent({
        listing: this.props.listing,
        componentName: this.props.trackingName,
        displayStyle: DISPLAY_STYLE,
        position: this.props.position,
        listingsCount: this.props.listingsCount,
        city: this.props.listing.shop.address?.locality,
        region: this.props.listing.shop.address?.region,
        countryCode: this.sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(this.sellerCountryCode, this.props.user?.countryCode),
      }),
    };
  }

  get sellerCountryCode() {
    return this.props.listing.shop.address?.country?.countryCode;
  }

  renderProximityShopLocation() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress) return null;

    if (showProximityContent(shopAddress, this.props.user, this.props.location)) {
      return (
        <div data-proximity-location className="size-80 color-secondary">
          {I18n.t('commons.listingCard.location.sameCountryUs', {
            city: shopAddress.locality, state: shopAddress.region,
          })}
        </div>
      );
    }

    return null;
  }

  renderShopLocationCountryFlag() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress || !shopAddress.country) return null;

    if (this.props.showFlag || showFlagForMarketplace(this.props.user, this.props.location)) {
      return (
        <div>
          <ListingShopCountryNudge
            data-shop-country-nudge
            country={shopAddress.country}
          />
        </div>
      );
    }

    return null;
  }

  shopLocation() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress) return '';

    if (showProximityContent(shopAddress, this.props.user, this.props.location)) {
      return I18n.t('commons.listingCard.location.sameCountryUs', {
        city: shopAddress.locality, state: shopAddress.region,
      });
    }

    return '';
  }

  showLocalPickupNudge() {
    const { listing: { shop }, user, location } = this.props;
    const localPickupAvailable = this.decoratedListing.localPickupAvailable && !this.decoratedListing.localPickupOnly;

    return localPickupAvailable && showProximityContent(shop?.address, user, location);
  }

  nudges(): RCNudgeProps[] {
    const { location, user, listing, displayGreatValue, showFlag } = this.props;
    const shopAddress = listing.shop?.address;
    const includeFlagNudge = showFlag || showFlagForMarketplace(user, location);

    const config = {
      displayMultiple: true,
      greatValue: displayGreatValue && isGreatValue(listing, user),
      inOtherCartsCount: listing?.otherBuyersWithListingInCartCounts,
      localPickupOnly: this.decoratedListing.localPickupOnly,
      localPickupAvailable: this.showLocalPickupNudge(),
      returnPolicyDays: this.returnPolicyDays(),
      newListing: isNewListing(listing) && isMarketplacePage(location),
      country: includeFlagNudge ? shopAddress.country : undefined,
    };

    return getNudges(config);
  }

  renderNudges() {
    const { listing, location } = this.props;

    // Signal system currently not in use due to performance concerns
    if (listing.signals) {
      return <ListingCardSignals listing={listing} />;
    }

    return (
      <ListingCardNudge
        freeExpeditedShipping={this.decoratedListing.freeExpeditedShippingAvailable}
        freeShipping={this.decoratedListing.freeStandardShippingAvailable}
        greatValue={this.props.displayGreatValue && isGreatValue(listing, this.props.user)}
        inOtherCartsCount={listing?.otherBuyersWithListingInCartCounts}
        localPickupAvailable={this.showLocalPickupNudge()}
        localPickupOnly={this.decoratedListing.localPickupOnly}
        returnPolicyDays={this.returnPolicyDays()}
        newListing={isNewListing(listing) && isMarketplacePage(location)}
        displayMultiple
      />
    );
  }

  getPricePropForRCCard(): RCPriceBlockProps {
    const { decoratedListing } = this;
    const { combinedShippingDiscount } = this.props;
    const showDiscountPrice = !combinedShippingDiscount && decoratedListing.isLive;

    return {
      priceDisplay: decoratedListing.primaryPriceDisplay,
      discountPriceDescription: decoratedListing.discountPriceDescription,
      priceNudge: getPriceNudgeProps(decoratedListing.priceNudge),
      shippingPromo: decoratedListing.shippingPromo,
      a11yPriceDescription: getA11yPriceDescription(decoratedListing),
      discountPriceDisplay: showDiscountPrice ? decoratedListing.discountedPriceDisplay : undefined,
      shippingDisplay: combinedShippingDiscount ? decoratedListing.primaryShippingDisplay : undefined,
      shippingStrike: !!combinedShippingDiscount,
    };
  }

  render() {
    const { decoratedListing } = this;
    const { listing, user } = this.props;
    const webTwoTheme = this.context?.theme === ThemeType.web2;

    if (!listing) {
      return <PlaceholderCard />;
    }

    const classes = classNames(
      'grid-card',
      { 'grid-card--software': decoratedListing.isDigital },
      { 'grid-card--discounted': this.hasDiscount() },
      { 'grid-card--with-cart-action': this.props.displayAddToCart },
    );

    const openNewTab = user && user.deviceName === WEB;
    const images = listing.images.map(i => (i.source));

    if (webTwoTheme) {
      return (
        <>
          <RCListingCard
            linkElement={(children) => (
              <CoreLink
                to={this.listingUrl()}
                target={openNewTab ? '_blank' : undefined}
                clickEvent={this.clickEvent()}
              >
                {children}
              </CoreLink>
            )}
            thumbnailElement={(<ImageCarousel images={images}/>)}
            title={listing.title}
            conditionLabel={decoratedListing.conditionLabel}
            shopLocation={this.shopLocation()}
            price={this.getPricePropForRCCard()}
            nudges={this.nudges()}
            fullHeight
            eyebrow={getEyebrow(listing)}
            // favorite=TODO
          />
          {this.props.footerContent}
        </>
      );
    }

    return (
      <div className={classes}>
        <CoreLink
          className="grid-card__inner"
          to={this.listingUrl()}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={this.clickEvent()}
        >
          {this.renderMetadata()}
          <div className="grid-card__main">
            <div className="grid-card__image">
              <ImageCarousel
                images={images}
              />
              {this.isBumped() &&
                <div className="grid-card__bump-tag">
                  <BumpTag />
                </div>
              }
              {listing.state !== 'live' &&
                <div className="grid-card__sold">
                  {listing.stateDescription}
                </div>
              }
            </div>
            <div className="grid-card__main__text">
              <h4 className="grid-card__title">
                {this.props.listing.title}
              </h4>
              {this.renderProximityShopLocation()}
            </div>
          </div>
          <div className="grid-card__footer">
            {this.renderOriginalPrice()}
            <div
              className="grid-card__footer__pricing"
            >
              <div className="grid-card__price">
                <ListingPrice
                  combinedShippingDiscount={this.props.combinedShippingDiscount}
                  listing={listing}
                />
              </div>
              <ListingCondition listing={listing} />
            </div>
            {this.renderShopLocationCountryFlag()}
            {this.renderNudges()}
            {this.props.footerContent}
          </div>
        </CoreLink>
        {this.props.displayAddToCart &&
          <AddToCartButton
            listing={this.props.listing}
            position={this.props.position}
            parentComponentName={this.props.trackingName}
          />
        }
        <div className="grid-card__watch">
          <WatchBadge
            listing={this.props.listing}
            displayStyle={DisplayStyle.ICON}
            parentComponentName={this.props.watchBadgeParentComponentName || this.props.trackingName}
            useToast
          />
        </div>
      </div>
    );
  }
}

function isListingDomesticToBuyer(sellerCountryCode?: string, buyerCountryCode?: string): boolean {
  if (!(sellerCountryCode && buyerCountryCode)) { return false; }
  return sellerCountryCode === buyerCountryCode;
}

function showFlagForMarketplace(user: Partial<IUser>, location: Location) {
  return user.countryCode !== 'US' && optionalPathnameIncludes(location, '/marketplace');
}

function optionalPathnameIncludes(location: Location, path: string): boolean {
  if (!location?.pathname) {
    return false;
  }

  return location?.pathname?.includes(path);
}

function showProximityContent(shopAddress: Address, user: Partial<IUser>, location: Location) {
  return isUsingProximityFeatures(location) &&
    shopAddress?.country?.countryCode === 'US' &&
    user.countryCode === 'US';
}

function ListingPrice(props: {
  listing: ListingCardListingFields,
  combinedShippingDiscount: boolean,
}) {
  const { listing, combinedShippingDiscount } = props;
  const decoratedListing = buildListingForBuyer(listing);

  if (combinedShippingDiscount) {
    return (
      <PriceWithShipping
        listing={listing}
        showCombinedRate
        useCombinedShippingStrikethrough
        hideOriginalPrice
      />
    );
  }

  return (
    <PriceDisplay
      amountCents={listing.pricing.buyerPrice.amountCents}
      currency={listing.pricing.buyerPrice.currency}
      display={listing.pricing.buyerPrice.display}
      auction={decoratedListing.isAuction}
    />
  );
}

function ListingCondition(props: { listing: ListingCardListingFields }) {
  const { listing } = props;
  const decoratedListing = buildListingForBuyer(listing);

  if (decoratedListing.isDigital) return null;
  if (!listing.condition) return null;

  return (
    <div className="grid-card__condition">
      <ConditionDisplay
        slug={listing.condition.conditionSlug}
        displayName={listing.condition.displayName}
        textOnly
      />
    </div>
  );
}

export function PlaceholderCard() {
  return (
    <div className="grid-card grid-card--placeholder">
      <div className="grid-card__inner">
        <div className="grid-card__main">
          <div className="grid-card__image" />
          <div className="grid-card__main__text">
            <div className="grid-card__title" />
          </div>
        </div>

        <div className="grid-card__footer">
          <div className="grid-card__footer__pricing">
            <div className="grid-card__price" />
          </div>
        </div>
      </div>
    </div>
  );
}

export const ListingCardFragment = gql`
  fragment ListingCardFields on Listing {
    _id
    ...ListingForBuyerFields
    ...WatchBadgeData
  }
  ${ListingForBuyerFragment}
  ${WatchBadgeFragment}
`;

export default withRouter(withUserContext(ListingCard));
